import type { Card } from "../gen/bazivan/v1/table_pb";
import { cardName, isRed, rankLabel, suitSymbol } from "../game/cards";
import { avatarSrc } from "../game/avatar";
import { SUBSCRIBER_MARK } from "../game/seats";
import { lerp, outCubic } from "./ease";
import { isPickKey, nextHandIndex } from "./handkeys";
import { flyCard, kill, tween, tweenRaw, wait } from "./fly";
import {
  CARD_ASPECT, CHAIR_RING, cardW, deckAnchor, handCardTarget, pileCardScale, place, playerCardW,
  playerHandTarget, screen, seatAngleDeg, seatAnchor, seatPos, type Screen, type Vec,
} from "./geometry";

/**
 * صحنه‌ی پایه‌ی میز — پورتِ بخشِ مشترکِ TableScene + آن‌چه HokmView و GameView
 * یک‌جور می‌سازند: بومِ ۷۲۰ واحدی، لایه‌ها، صندلی و فرش، نشست‌ها (آواتار، حلقه‌ی
 * نوبت، پلاکِ حریف، پلاکِ نامِ خودم)، تایمرِ نوبت، توست، پالس، لرزش، و
 * انیمیشن‌های مشترک (آس‌کشی، بُر، پخش، کشیدن).
 *
 * DOM را **خودش** می‌کشد، نه ری‌اکت؛ دلیلش همان دلیلِ یونیتی است: صحنه یک صفِ
 * انیمیشن دارد و هر گام کارت می‌سازد، پرواز می‌دهد و می‌کُشد. اگر ری‌اکت هم
 * هم‌زمان وضعیت را بکشد، دو حقیقت سرِ یک میز است.
 *
 * **واحدها همان بومِ یونیتی‌اند**: CanvasScaler با مرجعِ ۷۲۰ روی ضلعِ کوتاه.
 * ریشه به همان اندازه ساخته و با `scale()` روی ظرف می‌نشیند، پس هر عددِ
 * نماهای اپ این‌جا هم همان عدد است.
 */
export const UNIT = 720;                      // Bootstrap: referenceResolution روی ضلعِ کوتاه
export const CHROME_D = 104, CHROME_M = 16;   // TableScene.ChromeD / ChromeM
export const GLOW = "#ffe9a8", CRIMSON = "#b3443a", BRASS = "#c9a24d", BRASS_SOFT = "#d9b55c", IVORY = "#f5ecd4", OK = "#4fbf7a", AZURE = "#4a7fb5";

export type SeatUi = {
  root: HTMLElement; ring: HTMLElement; av: HTMLElement;
  count: HTMLElement | null; score: HTMLElement | null; name: HTMLElement | null;
  pos: Vec; d: number;
};

export type SeatData = {
  names: string[]; bots: boolean[]; avatars: string[]; subscribers: boolean[]; connected: boolean[];
  /** برچسبِ جا (شهر/کشور) زیرِ نام — خالی اگر نگفته یا نشان نداده. */
  places?: string[];
  /**
   * نمای تماشاچی: صندلیِ ۰ «من» نیست.
   *
   * آن‌وقت صندلیِ پایین هم مثلِ بقیه روی حلقه می‌نشیند و پلاکِ اطلاعات
   * می‌گیرد؛ وگرنه بازیکنی که تماشایش می‌کنی بی‌نام و بی‌شمارِ کارت پایینِ
   * صفحه می‌مانْد، انگار جای خالیِ خودت باشد.
   */
  watching?: boolean;
};

// TableScene.NameFontSize / NamePlateH
export const nameFontSize = (d: number) => Math.max(12, d * 0.16);
export const namePlateH = (d: number) => Math.max(26, nameFontSize(d) * 1.85);
export function norm(v: Vec): Vec { const l = Math.hypot(v.x, v.y); return l > 1 ? { x: v.x / l, y: v.y / l } : { x: 0, y: 1 }; }

export abstract class TableView {
  readonly root: HTMLElement;
  protected host: HTMLElement;
  protected sc!: Screen;
  protected u = 1;                            // پیکسلِ CSS به ازای هر واحدِ بوم
  protected count: number;

  protected table!: HTMLElement; protected opp!: HTMLElement; protected piles!: HTMLElement;
  protected seats!: HTMLElement; protected hand!: HTMLElement; protected meLayer!: HTMLElement;
  protected fly!: HTMLElement; protected ui!: HTMLElement; protected ov!: HTMLElement;

  protected seatEls: SeatUi[] = [];
  protected handHidden = false;
  private timerChip: HTMLElement | null = null;

  /** قطرِ آواتار به نسبتِ شعاعِ میز — حکم ۰٫۲۶، هفت‌خبیث ۰٫۳۲. */
  protected abstract seatDFactor(): number;
  /** بلندیِ پلاکِ حریف — حکم ۵۸ (نام+شمار)، هفت‌خبیث ۷۶ (نام+شمار+امتیاز). */
  protected abstract plateH(): number;
  /** بعد از ساختِ لایه‌ها: زیرکلاس مرکزِ میز و کرومِ خودش را می‌سازد. */
  protected abstract buildCenter(): void;
  /** بعد از تغییرِ اندازه: زیرکلاس وضعیتِ آخر را دوباره می‌کشد. */
  protected abstract redraw(): void;
  /** زیرکلاس می‌تواند به نشست چیزی بیفزاید (نشانِ حاکم و…). */
  protected decorateSeat(_l: number, _root: HTMLElement, _d: number, _pos: Vec): void {}

  constructor(host: HTMLElement, count: number, className: string) {
    this.host = host;
    this.count = Math.max(2, Math.min(6, count));
    this.root = document.createElement("div");
    this.root.className = `tv ${className}`;
    host.appendChild(this.root);
    // build() را زیرکلاس بعد از پرکردنِ فیلدهایش صدا می‌زند: با معناشناسیِ
    // define در ES2022، فیلدهای زیرکلاس **بعد از** super() تعریف می‌شوند و هر
    // چه build() در سازنده‌ی پایه روی آن‌ها نوشته بود پاک می‌شد.
  }

  get scr(): Screen { return this.sc; }
  destroy(): void { this.root.remove(); }

  // ---------------------------------------------------------------- ساخت

  /**
   * یک لایه‌ی تمام‌قدِ صحنه.
   *
   * نام حتماً پیشوندِ `tv-` می‌گیرد. بی آن، لایه‌ی «table» با قاعده‌ی سراسریِ
   * `html.art .table` جور می‌شد و فرشِ میز را **یک بار دیگر** به‌عنوانِ
   * پس‌زمینه‌ی خودش می‌کشید — دو میز روی هم. همان دام برای hand/banner/face
   * هم هست، پس هیچ نامِ لُختی این‌جا نمی‌آید.
   */
  protected layer(name: string): HTMLElement {
    const l = document.createElement("div");
    l.className = `tv-layer tv-${name}`;
    this.root.appendChild(l);
    return l;
  }

  protected build(): void {
    this.root.innerHTML = "";
    const r = this.host.getBoundingClientRect();
    const w = r.width || 390, h = r.height || 700;
    this.u = Math.min(w, h) / UNIT;
    const W = w / this.u, H = h / this.u;
    this.root.style.width = `${W}px`; this.root.style.height = `${H}px`;
    this.root.style.transform = `scale(${this.u})`;
    this.sc = screen(W, H);

    this.table = this.layer("table");
    // صندلی‌های حریف‌ها (Chair) روی حلقه‌ی بیرونی — قطر ۰٫۴R
    for (let i = 1; i < this.count; i++) {
      const p = seatPos(this.sc, i, this.count, this.sc.tableR * CHAIR_RING);
      const d = this.sc.tableR * 0.4;
      const chair = document.createElement("div");
      chair.className = "tv-chair";
      chair.style.width = chair.style.height = `${d}px`;
      chair.style.transform = place(p);
      chair.innerHTML = `<i style="width:${d * 0.66}px;height:${d * 0.66}px;transform:translate(${-d * 0.08}px,${-d * 0.08}px)"></i>`;
      this.table.appendChild(chair);
    }
    // فرش (TableScene.Carpet): بیضی به شعاعِ R×aspect در R، با سایه، حاشیه و عمقِ مرکز
    {
      const R = this.sc.tableR, rx = R * this.sc.aspect;
      const carpet = document.createElement("div");
      carpet.className = "tv-carpet";
      carpet.style.width = `${rx * 2}px`; carpet.style.height = `${R * 2}px`;
      carpet.style.transform = place({ x: 0, y: 0 });
      carpet.innerHTML =
        `<i class="shadow" style="inset:${-R * 0.05}px;transform:translateY(${R * 0.03}px)"></i>` +
        `<i class="rim" style="inset:-3px"></i><i class="art"></i>` +
        `<i class="depth" style="width:${rx * 1.2}px;height:${R * 1.2}px"></i>`;
      this.table.appendChild(carpet);
    }

    this.opp = this.layer("opp");
    this.piles = this.layer("piles");
    this.seats = this.layer("seats");
    this.hand = this.layer("hand");
    // «تمرکز داخلِ دست بود» را خودمان نگه می‌داریم: هر render دستِ قبلی را پاک
    // می‌کند، و آن‌وقت activeElement بدنه است و دیگر نمی‌شود پرسید.
    this.hand.addEventListener("focusout", (e) => {
      const to = (e as FocusEvent).relatedTarget as Node | null;
      // relatedTarget تهی یعنی کارت از DOM رفت (نه این‌که کاربر جای دیگری رفت)
      if (to && !this.hand.contains(to)) this.handFocused = false;
    });
    this.meLayer = this.layer("me");
    this.fly = this.layer("fly");
    this.ui = this.layer("ui");
    this.ov = this.layer("ov");
    this.timerChip = null;
    this.buildCenter();
  }

  /** بازسازیِ چیدمان سرِ چرخش/تغییرِ اندازه — RebuildLayout. */
  relayout(): void { this.build(); this.redraw(); }

  // ---------------------------------------------------------------- کارت

  /** همان مارک‌آپِ CardView تا CSSِ ورق (و بسته‌ی آرت) این‌جا هم کار کند. */
  protected cardEl(card: Card | null | undefined, faceDown: boolean, width: number, special = 0): HTMLElement {
    const el = document.createElement("div");
    const red = card && !faceDown && !special && isRed(card.suit);
    el.className = `card ${faceDown ? "back" : ""} ${special ? `special sp${special}` : ""} ${red ? "red" : ""}`;
    el.style.setProperty("--cw", `${width}px`);
    if (!faceDown && special) {
      el.innerHTML = `<span class="spf"><span class="g">${SPECIAL_GLYPH[special] ?? ""}</span><span class="n">${SPECIAL_NAME[special] ?? ""}</span></span>`;
    } else if (card && !faceDown) {
      el.style.setProperty("--col", String(card.rank - 1));
      el.style.setProperty("--row", String(card.suit - 1));
      el.innerHTML = `<span class="idx"><span class="r">${rankLabel(card.rank)}</span><span class="s">${suitSymbol[card.suit]}</span></span><span class="pip">${suitSymbol[card.suit]}</span>`;
    }
    return el;
  }

  // ------------------------------------------------------- دست با صفحه‌کلید

  /**
   * دستِ خودم، قابلِ بازی با صفحه‌کلید.
   *
   * کارت‌ها `div`ِ خالی با شنونده‌ی click بودند: با ماوس و انگشت کار می‌کردند و
   * با صفحه‌کلید هیچ — نه Tab به آن‌ها می‌رسید، نه Enter کاری می‌کرد، و
   * صفحه‌خوان هم چیزی برای گفتن نداشت. یعنی بازی بی‌اشاره‌گر **بازی نمی‌شد**.
   *
   * کلِ بادبزن یک ایستگاهِ Tab است و نه سیزده تا (tabindex چرخشی)؛ داخلش با
   * کلیدهای جهت می‌روی و با Enter/Space می‌زنی — همان کاری که با انگشت می‌کنی.
   */
  protected handFocused = false;
  private handIdx = 0;

  protected armCard(el: HTMLElement, label: string, onPick: () => void): void {
    el.tabIndex = -1;
    el.setAttribute("role", "button");
    el.setAttribute("aria-label", label);
    el.dataset.pick = "1";
    el.addEventListener("click", onPick);
    el.addEventListener("focus", () => {
      this.handFocused = true;
      const i = this.armedCards().indexOf(el);
      if (i >= 0) this.handIdx = i;
    });
    el.addEventListener("keydown", (e) => {
      if (isPickKey(e.key)) { e.preventDefault(); onPick(); return; }
      const cards = this.armedCards();
      const j = nextHandIndex(cards.indexOf(el), e.key, cards.length);
      if (j === null) return;
      e.preventDefault();
      this.focusHandCard(j);
    });
  }

  /** نامِ گفتنیِ کارت برای صفحه‌خوان. کارتِ ویژه‌ی هفت خبیث نامِ خودش را دارد. */
  protected cardLabelFor(card: Card | null | undefined, special = 0): string {
    if (special) return SPECIAL_NAME[special] ?? "کارتِ ویژه";
    return card ? cardName(card) : "کارت";
  }

  private armedCards(): HTMLElement[] {
    return [...this.hand.querySelectorAll<HTMLElement>("[data-pick]")];
  }

  /**
   * بعد از هر بازکشیدنِ دست صدا زده می‌شود: یک کارت ایستگاهِ Tab می‌شود، و اگر
   * تمرکز پیش از بازکشیدن داخلِ دست بود، همان‌جا برمی‌گردد — وگرنه بعد از هر
   * کارتی که می‌زنی باید از اولِ صفحه دوباره Tab می‌زدی.
   */
  protected armHand(): void {
    const cards = this.armedCards();
    if (cards.length === 0) return;
    this.handIdx = Math.max(0, Math.min(this.handIdx, cards.length - 1));
    cards.forEach((c, i) => { c.tabIndex = i === this.handIdx ? 0 : -1; });
    if (this.handFocused) cards[this.handIdx]?.focus();
  }

  private focusHandCard(i: number): void {
    const cards = this.armedCards();
    this.handIdx = Math.max(0, Math.min(i, cards.length - 1));
    cards.forEach((c, k) => { c.tabIndex = k === this.handIdx ? 0 : -1; });
    cards[this.handIdx]?.focus();
  }

  protected flyEl(card: Card | null | undefined, faceDown: boolean, width: number, special = 0): HTMLElement {
    const el = this.cardEl(card, faceDown, width, special);
    this.fly.appendChild(el);
    return el;
  }

  protected deck(): Vec { return deckAnchor(this.sc); }
  protected seatFx(l: number): Vec { return this.seatEls[l]?.pos ?? seatAnchor(this.sc, l, this.count); }
  /** جایی که کارتِ خودم از آن پرواز می‌کند: لبه‌ی پایین، نیم‌کارت بالاتر. */
  protected myOrigin(): Vec { return { x: 0, y: -this.sc.halfH + playerCardW(this.sc) * CARD_ASPECT * 0.5 }; }

  /** جای فعلیِ عنصر از transform — برای شروعِ پروازِ بازگشت. */
  protected currentPos(el: HTMLElement): Vec {
    const m = /translate\(calc\(-50% \+ ([-\d.]+)px\), calc\(-50% \+ ([-\d.]+)px\)\)/.exec(el.style.transform);
    return m ? { x: Number(m[1]), y: -Number(m[2]) } : { x: 0, y: 0 };
  }

  // ---------------------------------------------------------------- نشست‌ها

  /** PlayerAvatarPos: آواتارِ خودم پایینِ صفحه و وسط، سرش پشتِ بادبزن. */
  protected playerAvatarPos(): Vec {
    const R = this.sc.tableR;
    const cardH = playerCardW(this.sc) * CARD_ASPECT;
    const up = cardH * 0.35;
    const dMax = 0.32 * R;
    const floorUp = dMax * 0.6 + namePlateH(dMax) + 8;
    const ceilUp = Math.max(floorUp + 1, 150);
    return { x: 0, y: -this.sc.halfH + Math.min(ceilUp, Math.max(floorUp, up)) };
  }

  /** InfoPlatePos: پلاکِ حریف بالای سرِ بالانشین‌ها، زیرِ بقیه؛ هرگز روی آواتار. */
  protected infoPlatePos(seat: Vec, d: number, pw = 118, ph = 76): Vec {
    const { halfW, halfH } = this.sc;
    // «بالای میز» با آستانه، نه با علامت: sin(π) در جاوااسکریپت ۱e-16 است و نشستِ چپ را بالانشین می‌کرد
    const above = seat.y > 0.5 && seat.y + d / 2 + ph + 10 < halfH;
    let py = above ? seat.y + d / 2 + ph / 2 + 6 : seat.y - (d / 2 + ph / 2 + 6);
    py = Math.min(halfH - ph / 2 - 8, Math.max(-halfH + ph / 2 + 8, py));
    return { x: Math.min(halfW - pw / 2 - 8, Math.max(-halfW + pw / 2 + 8, seat.x)), y: py };
  }

  protected layoutSeatsFrom(sd: SeatData, meLabel = "من"): void {
    // یک‌بار این‌جا نگه داشته می‌شود تا پرواز‌های کارت هم بدانند صندلیِ ۰ «من»
    // نیست: پخش و کشیدنِ کارتِ آن صندلی باید به خودش برود، نه به لبه‌ی پایین.
    this.watching = !!sd.watching;
    this.seats.innerHTML = ""; this.meLayer.innerHTML = "";
    this.seatEls = [];
    this.timerChip = null;
    const R = this.sc.tableR;
    const d = R * this.seatDFactor();
    for (let l = 0; l < this.count; l++) {
      const asMe = l === 0 && !sd.watching;
      const pos = asMe ? this.playerAvatarPos() : seatPos(this.sc, l, this.count, R * CHAIR_RING);
      const root = document.createElement("div");
      root.className = `tv-seat ${asMe ? "me" : ""}`;
      root.dataset.seat = String(l);
      root.style.width = root.style.height = `${d}px`;
      root.style.transform = place(pos);

      // حلقه‌ی نوبت (TurnRing): Stretch(-d·0.26) → قطرِ ۱٫۵۲d، پشتِ آواتار
      const ring = document.createElement("i"); ring.className = "ring"; ring.style.inset = `${-d * 0.26}px`;
      const av = document.createElement("div"); av.className = "av";
      av.innerHTML = `<img src="${avatarSrc(sd.avatars[l])}" alt="">`;
      root.append(ring, av);
      this.decorateSeat(l, root, d, pos);

      let count: HTMLElement | null = null, score: HTMLElement | null = null, name: HTMLElement | null = null;
      // تماشاچی «حریف» ندارد: هیچ‌کدامِ این چهار نفر حریفِ او نیستند.
      const unnamed = asMe ? meLabel : sd.watching ? "بازیکن" : "حریف";
      const nm = (sd.bots[l] ? "🤖 " : "") + (sd.names[l] || unnamed) + (sd.subscribers[l] ? " " + SUBSCRIBER_MARK : "");
      if (asMe) {
        // پلاکِ نام زیرِ آواتار (Avatar withName)
        const fs = nameFontSize(d), h = namePlateH(d);
        name = document.createElement("div"); name.className = "nameplate";
        name.style.cssText = `width:${d * 1.35}px;height:${h}px;font-size:${fs}px;border-radius:${h * 0.42}px;top:calc(100% + ${d * 0.1}px)`;
        name.textContent = sd.names[0] || meLabel;
        root.appendChild(name);
      } else {
        // پنلِ اطلاعاتِ حریف — بیرونِ میز
        const ph = this.plateH();
        let pp = this.infoPlatePos(pos, d, 118, ph);
        if (pos.y > 0.5 && pp.y > pos.y) pp = { x: pp.x, y: pp.y + d * 0.34 };   // جای تاج/نشان
        const plate = document.createElement("div"); plate.className = "info";
        plate.style.height = `${ph}px`;
        plate.style.transform = `translate(-50%,-50%) translate(${pp.x - pos.x}px,${-(pp.y - pos.y)}px)`;
        name = document.createElement("div"); name.className = "nm"; name.textContent = nm;
        // جای بازیکن، اگر خودش خواسته: ریز و زیرِ نام، تا با نام رقابت نکند.
        const plc = sd.places?.[l] ?? "";
        if (plc) { const p = document.createElement("div"); p.className = "plc"; p.textContent = plc; name.append(p); plate.classList.add("placed"); }
        count = document.createElement("div"); count.className = "cnt";
        plate.append(name, count);
        if (ph >= 70) { score = document.createElement("div"); score.className = "scr"; plate.appendChild(score); }
        root.appendChild(plate);
      }
      root.classList.toggle("off", !sd.connected[l]);
      (asMe ? this.meLayer : this.seats).appendChild(root);
      this.seatEls.push({ root, ring, av, count, score, name, pos, d });
    }
  }

  /** لمسِ صندلی (هدفِ ۲، مچ‌گیری) — زیرکلاس وصل می‌کند. */
  protected onSeatClick(fn: (l: number) => void): void {
    for (let l = 1; l < this.seatEls.length; l++) {
      const s = this.seatEls[l]!;
      s.root.style.pointerEvents = "auto";
      s.root.addEventListener("click", () => fn(l));
    }
  }

  protected setRing(l: number, on: boolean, danger = false): void {
    const s = this.seatEls[l]; if (!s) return;
    s.ring.classList.toggle("on", on);
    s.ring.classList.toggle("danger", danger);
  }

  protected setConnected(l: number, on: boolean): void { this.seatEls[l]?.root.classList.toggle("off", !on); }

  /** TurnTimer: دایره‌ی پُرشونده پشتِ آواتارِ خودم؛ سبز → برنزی → سرخ. */
  setTimer(remaining: number, total: number): void {
    if (!(total > 0 && remaining > 0)) { kill(this.timerChip); this.timerChip = null; return; }
    if (!this.timerChip) {
      const d = this.sc.tableR * this.seatDFactor();
      const chip = document.createElement("div");
      chip.className = "tv-timer";
      chip.style.width = chip.style.height = `${d}px`;
      chip.style.transform = place(this.playerAvatarPos());
      this.meLayer.prepend(chip);
      this.timerChip = chip;
    }
    const f = Math.min(1, Math.max(0, remaining / total));
    const col = f > 0.5 ? OK : f > 0.25 ? BRASS : CRIMSON;
    this.timerChip.style.setProperty("--f", String(f));
    this.timerChip.style.setProperty("--tc", col);
  }

  // ---------------------------------------------------------------- کمکی‌های افکت

  /** Pulse: دایره‌ی منبسط‌شونده که محو می‌شود (زمانِ خام، مثلِ یونیتی). */
  protected pulse(pos: Vec, color: string, from: number, to: number, durMs: number, alpha: number): Promise<void> {
    const el = document.createElement("div");
    el.className = "tv-pulse";
    el.style.background = color;
    this.ov.appendChild(el);
    return tweenRaw(durMs, (k) => {
      const p = outCubic(k), s = lerp(from, to, p);
      el.style.width = el.style.height = `${s}px`;
      el.style.opacity = String(alpha * (1 - p));
      el.style.transform = place(pos);
    }).then(() => kill(el));
  }

  toast(msg: string): void {
    const t = document.createElement("div");
    t.className = "tv-toast";
    t.textContent = msg;
    this.ov.appendChild(t);
    setTimeout(() => kill(t), 1800);
  }

  /**
   * Juice.HitStop: فریزِ کوتاهِ ۲۰ تا ۱۲۰ میلی‌ثانیه‌ای تا ضربه «حس» شود.
   *
   * در یونیتی `Time.timeScale` صفر می‌شود، و چون خودِ انیمیشنِ ضربه روی زمانِ
   * ناوابسته می‌دود، آن‌چه می‌ایستد حرکت‌های زمینه است. این‌جا هم همان: تابِ
   * بی‌کاریِ دستِ خودت یک لحظه می‌ایستد و ضربه روی سکون می‌نشیند.
   */
  protected hitStop(ms = 60): void {
    this.root.classList.add("froze");
    setTimeout(() => this.root.classList.remove("froze"), Math.min(120, Math.max(20, ms)));
  }

  /** Juice.Shake: لرزشِ میرای یک عنصرِ درونِ صحنه (مثلاً نمدِ میز). */
  protected shakeEl(el: HTMLElement, amp: number, durMs: number): Promise<void> {
    const seed = Math.random() * 100;
    // نوفه‌ی نرمِ دو‌موجی به‌جای PerlinNoise — لرزه باید بلرزد، نه بپرد
    const noise = (o: number, x: number) => (Math.sin(x * 11 + o) + Math.sin(x * 19.3 + o * 1.7)) * 0.5;
    return tween(durMs, (k) => {
      const a = amp * (1 - k);
      el.style.translate = `${noise(seed, k) * a}px ${noise(seed + 7, k) * a}px`;
    }).then(() => { el.style.translate = ""; });
  }

  /** ScreenShake: لرزشِ میرای ریشه. */
  protected shake(durMs: number, amp: number): Promise<void> {
    return tweenRaw(durMs, (k) => {
      let f = 1 - k; f *= f;
      const x = (Math.random() * 2 - 1) * amp * f, y = (Math.random() * 2 - 1) * amp * f;
      this.root.style.translate = `${x * this.u}px ${y * this.u}px`;
      this.root.style.rotate = `${(Math.random() * 2 - 1) * 1.1 * f}deg`;
    }).then(() => { this.root.style.translate = ""; this.root.style.rotate = ""; });
  }

  // ---------------------------------------------------------------- انیمیشن‌های مشترک

  /** AnimStarterDraw: ورق‌ها دوری رو می‌شوند؛ آخری آس است و صاحبش شروع‌کننده. */
  async animStarterDraw(cards: Card[], from: number, first: number, firstName: string): Promise<void> {
    const w = cardW(this.sc) * pileCardScale(this.sc) * 0.85;
    const spawned: HTMLElement[] = [];
    for (let k = 0; k < cards.length; k++) {
      const seat = (from + k) % this.count;
      const round = Math.floor(k / this.count);
      const angle = seatAngleDeg(seat, this.count), rad = angle * Math.PI / 180;
      const target = { x: Math.cos(rad) * this.sc.tableR * 0.38 * this.sc.aspect + round * 14,
                       y: Math.sin(rad) * this.sc.tableR * 0.38 - round * 10 };
      const face = this.flyEl(cards[k]!, false, w);
      spawned.push(face);
      await flyCard(face, this.deck(), target, angle + 90, 230);
      await wait(130);
    }
    void this.pulse(this.seatFx(first), GLOW, this.sc.tableR * 0.2, this.sc.tableR * 0.9, 600, 0.5);
    for (let q = 0; q < this.count; q++) this.setRing(q, q === first);
    this.toast(`آس! شروع با ${firstName}`);
    await wait(1100);
    for (const el of spawned) void flyCard(el, this.currentPos(el), this.deck(), 0, 200);
    await wait(240);
    spawned.forEach(kill);
  }

  /** AnimShuffle: شش پشتِ کارت دو بار از هم باز و بسته می‌شوند. */
  async animShuffle(): Promise<void> {
    const deck = this.deck(), w = cardW(this.sc) * pileCardScale(this.sc);
    const cards = Array.from({ length: 6 }, () => this.flyEl(null, true, w));
    const lay = (k: number) => cards.forEach((c, i) => {
      const side = i % 2 === 0 ? -1 : 1;
      c.style.transform = place({ x: deck.x + side * k * (26 + i * 9), y: deck.y + (i - cards.length / 2) * 5 * k }, side * k * 13);
    });
    for (let pass = 0; pass < 2; pass++) {
      await tweenRaw(160, (k) => lay(outCubic(k)));
      await tweenRaw(140, (k) => lay(1 - k));
    }
    cards.forEach(kill);
    await wait(120);
  }

  /**
   * اعلانِ «شروع» — یک تپشِ کوتاه پیش از اولین نوبت.
   *
   * بی این، پخشِ ورق تمام می‌شد و میز بی هیچ نشانه‌ای دستِ بازیکن می‌افتاد:
   * همان حسِ «یکهو رسیدیم به این‌که باید بازی کنم». یک بیت فاصله بینِ
   * «مراسم تمام شد» و «نوبتِ توست» کافی است.
   */
  async animStartBanner(text: string): Promise<void> {
    const g = document.createElement("div");
    g.className = "tbl-start";
    g.textContent = text;
    g.style.fontSize = `${this.sc.tableR * 0.24}px`;
    this.ov.appendChild(g);
    await tween(820, (k) => {
      const s = lerp(0.72, 1.06, outCubic(Math.min(1, k * 3.2)));
      g.style.transform = place({ x: 0, y: 0 }, 0, s);
      g.style.opacity = String(k < 0.18 ? outCubic(k / 0.18) : k > 0.72 ? 1 - (k - 0.72) / 0.28 : 1);
    });
    kill(g);
  }

  /** AnimDeal/AnimDealBatch: perPlayer پشتِ کارت برای هر صندلی، از startSeat، ۲۴۰ پرواز و ۵۵ فاصله. */
  /** نمای تماشاچی — صندلیِ ۰ هم یکی از بازیکن‌هاست. [[SeatData.watching]] */
  protected watching = false;

  protected async dealBacks(startSeat: number, perPlayer: number): Promise<void> {
    const flying: HTMLElement[] = [];
    const cw = cardW(this.sc), pw = playerCardW(this.sc);
    for (let n = 0; n < perPlayer; n++) {
      for (let k = 0; k < this.count; k++) {
        const p = (startSeat + k) % this.count;
        const mine = p === 0 && !this.watching;
        const t = mine ? playerHandTarget(this.sc, perPlayer, n) : handCardTarget(this.sc, p, this.count, perPlayer, n);
        const c = this.flyEl(null, true, mine ? pw : cw);
        flying.push(c);
        void flyCard(c, this.deck(), t.pos, t.rot, 240);
        await wait(55);
      }
    }
    await wait(300);
    flying.forEach(kill);
  }

  /** جای پروازِ کارتِ یک صندلی: دستِ خودم لبه‌ی پایین، بقیه روی حلقه. */
  protected seatOrigin(l: number): Vec {
    return l === 0 && !this.watching ? this.myOrigin() : seatAnchor(this.sc, l, this.count);
  }

  /** AnimDrawOne: یک پشتِ کارت از دسته به صندلی، ۲۴۰. */
  async animDrawOne(to: number): Promise<void> {
    const c = this.flyEl(null, true, cardW(this.sc) * pileCardScale(this.sc));
    await flyCard(c, this.deck(), this.seatOrigin(to), 0, 240);
    kill(c);
  }

  /**
   * AnimPass: کارتِ روبسته از دستِ یکی به دستِ دیگری — «تعارفِ ۲».
   *
   * بی این، کارت بی‌صدا جابه‌جا می‌شد و فقط شمارنده‌ها می‌پریدند: بازیکن
   * می‌دید یک برگ کم و زیاد شده و نمی‌فهمید چرا.
   */
  async animPass(from: number, to: number): Promise<void> {
    const c = this.flyEl(null, true, cardW(this.sc) * pileCardScale(this.sc));
    await flyCard(c, this.seatOrigin(from), this.seatOrigin(to), 0, 340);
    kill(c);
  }
}

/** کارت‌های ویژه‌ی هفت خبیث — همان ترتیبِ Bazivan.Core.Special و CardView وب. */
export const SPECIAL_NAME: Record<number, string> = { 1: "آینه", 2: "بمب", 3: "سپر", 4: "پنهان" };
export const SPECIAL_GLYPH: Record<number, string> = { 1: "◈", 2: "✸", 3: "⛨", 4: "◌" };
